import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, IconButton, Typography } from "@mui/material";
import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FaFileUpload, FaTimes, FaTrashAlt } from "react-icons/fa";
import { displayNotification, generateOptionsFromData } from "helpers";
import { FormDataContext } from "../contexts/FormContext";
import {
    useCreateElementAttachmentMutation,
    useDeleteElementAttachmentMutation,
    useGetElementAttachmentsQuery,
} from "modules/pvs_activities/redux/service/formAttachments";
import TextAreaField from "../core/TextAreaField";
import { capitalize } from "lodash";
import DatePickerCustom from "components/DatePickerCustom";
import ComponentLoader from "pages/components/ComponentLoader";
import SelectUploadedAttachmentModal from "./modals/SelectUploadedAttachmentModal";
import { useGetListByMasterKeyQuery, useLazyGetListByMasterKeyQuery } from "modules/pvs_activities/redux/service/lists";
import DropdownField from "../core/DropdownField";
import { pluralize } from "modules/pvs_activities/helper.general";
import { useGetTemplateQuery } from "modules/pvs_activities/redux/service/templates";
import { Mission_Document_type } from "modules/pvs_activities/constants";
const initialFormFields = {
    category_id: "",
    subcategory_id: "",
    date: "",
    ref_no: "",
    is_letter: false,
    label: "",
};

const defaultProps = {
    elementId: "",
    categoryId: "",
    subcategoryId: "",
    typeId: "",
    ccId: "",
    disabled: false,
    isReadOnly: false,
    label: "attachment",
};

const AttachmentField = (rawProps) => {
    const props = { ...defaultProps, ...rawProps };
    const fileInputRef = useRef();
    const formDataContext = useContext(FormDataContext);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState("");
    const [initialForm, setInitialForm] = useState({ ...initialFormFields });
    const [form, setForm] = useState({ ...initialForm });
    const templateRes = useGetTemplateQuery(formDataContext.templateId);
    const getAttachmentsRes = useGetElementAttachmentsQuery(
        { elementId: props.elementId, movementId: formDataContext.movementId, ccId: props.ccId },
        { skip: formDataContext.mode === "preview" }
    );
    const [createElementAttachment, createAttachmentRes] = useCreateElementAttachmentMutation();
    const [deleteElementAttachment, deleteAttachmentRes] = useDeleteElementAttachmentMutation();
    const categoryListRes = useGetListByMasterKeyQuery(
        {
            key: "MDOCCT",
            params: {
                order_by: "list_element_name,asc",
                parent_id: props.typeId ?? "0",

            },
        },
        { skip: props.typeId === "" }
    );
    const [subcategoryTrigger, subcategoryListRes] = useLazyGetListByMasterKeyQuery();

    useEffect(() => {
        let tmp = { ...form };
        let tmp1 = { ...initialForm };
        if (props.categoryId || props.subcategoryId) {
            subcategoryTrigger({
                key: "MDOCSC",
                params: {
                    parent_id:
                        parseInt(props.typeId) === Mission_Document_type.baseline_document
                            ? props.typeId
                            : props.categoryId ?? "0",
                    order_by: "list_element_name,asc",
                },
            });
            tmp = { ...tmp, category_id: props.categoryId, subcategory_id: props.subcategoryId };
            tmp1 = { ...tmp1, category_id: props.categoryId, subcategory_id: props.subcategoryId };
        }
        if (props.categoryId && props.subcategoryId === "") {
            tmp = { ...tmp, category_id: props.categoryId };
            tmp1 = { ...tmp1, category_id: props.categoryId };
        }
        if (props.categoryId === "" && props.subcategoryId) {
            tmp = { ...tmp, subcategory_id: props.subcategoryId };
            tmp1 = { ...tmp1, subcategory_id: props.subcategoryId };
        }
        setForm(tmp);
        setInitialForm(tmp1);
    }, [props.categoryId, props.subcategoryId]);

    useEffect(() => {
        if (createAttachmentRes.isError) {
            displayNotification("error", `Error uploading ${props.label}`);
            console.error({ error: createAttachmentRes.error });
            setIsUploading(false);
        }
        if (createAttachmentRes.isSuccess) {
            displayNotification("success", `Successfully uploaded ${props.label}`);
            setForm({ ...initialForm });
            setSelectedFile("");
            setIsUploading(false);
        }
    }, [createAttachmentRes]);

    useEffect(() => {
        if (deleteAttachmentRes.isError) {
            displayNotification("error", `Error deleting ${props.label}`);
            console.error({ error: deleteAttachmentRes.error });
        }
        if (deleteAttachmentRes.isSuccess) {
            displayNotification("success", `Successfully deleted ${props.label}`);
        }
    }, [deleteAttachmentRes]);

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleFileUpload = () => {
        const requiredFields = ["description", "is_letter", "category_id", "subcategory_id", "label"];
        if (form.is_letter === true) {
            requiredFields.push("date");
            requiredFields.push("ref_no");
        }
        for (const x of requiredFields) {
            if (form[x] === "") {
                const displayField = x === "label" ? "Description" : capitalize(x.replace("_id", ""));
                displayNotification("warning", `Field "${displayField}" is required.`);
                return;
            }
        }
        if (formDataContext.mode === "preview") {
            displayNotification("success", "Successfully Uploaded File");
            handleReset();
            return;
        }
        setIsUploading(true);
        const data = new FormData();
        data.append("template_id", formDataContext.templateId);
        data.append("section_id", formDataContext.sectionId);
        data.append("element_id", props.elementId);
        data.append("movement_id", formDataContext.movementId);
        data.append("type_id", props.typeId);
        data.append("cc_id", props.ccId);
        for (const x in form) {
            data.append(x, form[x]);
        }
        data.append("attachment", selectedFile);
        createElementAttachment(data);
    };

    const handleDelete = (id) => {
        if (window.confirm(`You are about to delete an ${props.label}. Are you sure?`)) {
            deleteElementAttachment(id);
        }
    };

    const handleReset = () => {
        console.log("e");
        setForm({ ...initialForm });
        setSelectedFile("");
    };

    const categoriesOptions = useMemo(() => {
        return generateOptionsFromData(categoryListRes.data ?? [], {
            value: "id",
            label: "list_element_name",
        });
    }, [categoryListRes.data]);

    const subCategoriesOptions = useMemo(() => {
        return generateOptionsFromData(subcategoryListRes.data ?? [], {
            value: "id",
            label: "list_element_name",
        });
    }, [subcategoryListRes.data]);

    return (
        <Box sx={{ borderStyle: "dotted", borderColor: "#ccc" }}>
            {props.typeId === "" ? (
                <Alert severity="warning">Please configure {props.label} element correctly.</Alert>
            ) : (
                <>
                    {props.isReadOnly === false && (
                        <>
                            <Box sx={{ p: 2 }} className="bg-light-2">
                                <Grid container spacing={1}>
                                    {selectedFile ? (
                                        <>
                                            {
                                                parseInt(props.typeId) === Mission_Document_type.baseline_document ? (
                                                    <>
                                                        <Grid item xl={3} lg={3} md={3} sm={3} xs={12}>
                                                            <DropdownField
                                                                label="Select category"
                                                                plain={true}
                                                                required={true}
                                                                hasOptions={true}
                                                                options={categoriesOptions}
                                                                value={form.category_id}
                                                                onChange={(val) => {
                                                                    setForm({ ...form, category_id: val, subcategory_id: "" });
                                                                    // if (val !== "") {
                                                                    //     subcategoryTrigger({
                                                                    //         key: "MDOCSC",
                                                                    //         params: {
                                                                    //             parent_id: val ?? "0",
                                                                    //             order_by: "list_element_name,asc",
                                                                    //         },
                                                                    //     });
                                                                    // }
                                                                }}
                                                                disabled={isUploading}
                                                                size="small"
                                                                marginTop={0}
                                                                sx={{ backgroundColor: "#fff" }}
                                                            />
                                                        </Grid>
                                                        <Grid item xl={3} lg={3} md={3} sm={3} xs={12}>
                                                            <DropdownField
                                                                label="Select Sub-Category"
                                                                plain={true}
                                                                required={true}
                                                                hasOptions={true}
                                                                options={subCategoriesOptions}
                                                                value={form.subcategory_id}
                                                                onChange={(val) => setForm({ ...form, subcategory_id: val })}
                                                                disabled={isUploading}
                                                                size="small"
                                                                marginTop={0}
                                                                sx={{ backgroundColor: "#fff" }}
                                                            />
                                                        </Grid>
                                                    </>
                                                ) : null
                                            }


                                            {/* <Grid
                                                item
                                                xl={form.is_letter === true ? 2 : 6}
                                                lg={form.is_letter === true ? 2 : 6}
                                                md={form.is_letter === true ? 2 : 6}
                                                sm={form.is_letter === true ? 2 : 6}
                                                xs={12}
                                                sx={{ display: "flex" }}
                                            >
                                                <FormControlLabel
                                                    className="m-auto"
                                                    control={<Checkbox />}
                                                    value={true}
                                                    label={`Is Letter?`}
                                                    checked={form.is_letter}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            is_letter: e.target.checked,
                                                            ...(e.target.checked === false
                                                                ? { date: "", ref_no: "" }
                                                                : {}),
                                                        })
                                                    }
                                                    disabled={isUploading}
                                                    sx={{ backgroundColor: "#fff", pr: 1, width: "100%" }}
                                                    required={false}
                                                />
                                            </Grid> */}
                                            {form.is_letter === true && (
                                                <>
                                                    <Grid item xl={2} lg={2} md={2} sm={2} xs={12}>
                                                        <DatePickerCustom
                                                            required={true}
                                                            label={"Select Date"}
                                                            fullWidth
                                                            value={
                                                                !["", "Invalid date"].includes(form.date)
                                                                    ? form.date
                                                                    : null
                                                            }
                                                            onChange={(val) => setForm({ ...form, date: val })}
                                                            inputFormat="DD-MMM-YY"
                                                            size="small"
                                                            disabled={isUploading}
                                                            sx={{ width: "100%", backgroundColor: "#fff" }}
                                                            marginTop={0}
                                                        />
                                                    </Grid>
                                                    <Grid item xl={2} lg={2} md={2} sm={2} xs={12}>
                                                        <TextAreaField
                                                            required={true}
                                                            label={"Reference #"}
                                                            type={"text"}
                                                            value={form.ref_no}
                                                            onChange={(val) => setForm({ ...form, ref_no: val })}
                                                            disabled={isUploading}
                                                            size="small"
                                                            marginTop={0}
                                                            sx={{ backgroundColor: "#fff" }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            <Grid item xl={6} lg={6} md={6} sm={6} xs={12}>
                                                <TextAreaField
                                                    required={true}
                                                    label={"Description"}
                                                    type={"text"}
                                                    value={form.label}
                                                    onChange={(val) => setForm({ ...form, label: val })}
                                                    disabled={isUploading}
                                                    size="small"
                                                    marginTop={0}
                                                    sx={{ backgroundColor: "#fff" }}
                                                />
                                            </Grid>
                                            <Grid item xl={6} lg={6} md={6} sm={6} xs={12}>
                                                <div className="d-flex">
                                                    <div
                                                        className="px-3 py-2 flex-grow-1 d-inline-flex"
                                                        style={{
                                                            border: "1px solid #ccc",
                                                            borderRight: 0,
                                                            minWidth: 150,
                                                            overflow: "hidden",
                                                            backgroundColor: "#fff",
                                                        }}
                                                    >
                                                        {selectedFile && selectedFile.name}
                                                    </div>
                                                    <Button
                                                        variant={"contained"}
                                                        color="success"
                                                        sx={{ alignSelf: "stretch" }}
                                                        onClick={handleFileUpload}
                                                        disabled={isUploading}
                                                        disableElevation={true}
                                                    >
                                                        <FaFileUpload />
                                                        <span className="ms-1">
                                                            {isUploading ? "Uploading..." : "Upload"}
                                                        </span>
                                                    </Button>
                                                    <Button
                                                        variant={"contained"}
                                                        color="error"
                                                        sx={{ alignSelf: "stretch" }}
                                                        onClick={handleReset}
                                                        disabled={isUploading}
                                                        disableElevation={true}
                                                    >
                                                        <FaTimes />
                                                    </Button>
                                                </div>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid
                                            item
                                            xl={12}
                                            lg={12}
                                            md={12}
                                            sm={12}
                                            xs={12}
                                            sx={{ display: "flex", justifyContent: "start", gap: 2 }}
                                        >
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                                disabled={props.disabled}
                                            />
                                            <Button
                                                variant={"contained"}
                                                color="primary"
                                                sx={{ my: "auto" }}
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <FaFileUpload />
                                                <span className="ms-1">Browse File</span>
                                            </Button>
                                            {templateRes.data?.can_select_from_uploaded_document === true && (
                                                <>
                                                    <div className="my-auto mx-2">OR</div>
                                                    <SelectUploadedAttachmentModal
                                                        elementId={props.elementId}
                                                        selected={
                                                            getAttachmentsRes.isSuccess
                                                                ? getAttachmentsRes.data.map((r) => r.document_id)
                                                                : []
                                                        }
                                                        onChange={() => getAttachmentsRes.refetch()}
                                                    />
                                                </>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                            <hr className="m-0 p-0" style={{ borderColor: "#ccc", borderWidth: 2 }} />
                        </>
                    )}
                    {getAttachmentsRes.status === "uninitialized" ? (
                        <Alert severity="info">No {pluralize(2, props.label, "s")} found.</Alert>
                    ) : getAttachmentsRes.isError ? (
                        <Alert severity="error">
                            Error loading {pluralize(2, props.label, "s")}. Please reload the page.
                        </Alert>
                    ) : getAttachmentsRes.isLoading ? (
                        <ComponentLoader />
                    ) : getAttachmentsRes.isSuccess ? (
                        getAttachmentsRes.data.length === 0 || getAttachmentsRes.data === undefined ? (
                            <Alert severity="info">No {pluralize(2, props.label, "s")} found.</Alert>
                        ) : (
                            getAttachmentsRes.data.map((r) => (
                                <Fragment key={r.id}>
                                    <Box sx={{ p: 2, display: "flex" }}>
                                        <Box>
                                            <Typography variant="h6" title={`Description: ${r?.document?.label}`}>
                                                <span className="fw-bold">Description: </span>  {r?.document?.label}
                                            </Typography>
                                            <Typography variant="h6" sx={{ mt: 1 }} title={`Filename: ${r?.document?.actual_name}`}>
                                                <span className="fw-bold">Filename: </span> {r?.document?.actual_name}
                                            </Typography>
                                            <Typography variant={"body1"}>
                                                {/* <span>(Category: {r?.document?.category_name})</span>
                                                <span className="mx-1">-</span>
                                                <span>(Subcategory: {r?.document?.subcategory_name})</span> */}
                                                {r?.document?.is_letter === true && (
                                                    <>
                                                        <span className="mx-1">-</span>
                                                        <span>(Date: {r?.document?.file_date_formatted})</span>
                                                        <span className="mx-1">-</span>
                                                        <span>(Reference #: {r?.document?.ref_no})</span>
                                                    </>
                                                )}
                                            </Typography>
                                        </Box>
                                        {props.isReadOnly === false && (
                                            <Box sx={{ ml: "auto" }}>
                                                <IconButton color="error" onClick={() => handleDelete(r.id)}>
                                                    <FaTrashAlt />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                    <hr className="m-0 p-0" style={{ borderColor: "#ccc", borderWidth: 2 }} />
                                </Fragment>
                            ))
                        )
                    ) : (
                        ""
                    )}
                </>
            )}
        </Box>
    );
};

export default AttachmentField;
